import React, { useEffect, useState } from "react";
import {
  Bookmark, BookmarkPlus, Check, ChevronDown, Loader2,
  Trash2, X, Crown,
} from "lucide-react";
import { toast } from "sonner";
import {
  listChartLayouts, createChartLayout, deleteChartLayout,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Saved chart layouts — Pro-only TradingView preset manager.
 *
 * Persists `{name, symbol, interval, studies, notes}` to /api/chart-layouts.
 * Mounted in `SymbolChart` only when the user is on the "Pro" engine so the
 * UI matches what gets saved.
 *
 * Available indicators (subset of TradingView's free study list — these are
 * the ones that have a stable `@tv-basicstudies` reference and load without
 * requiring a TV account):
 */
const AVAILABLE_STUDIES = [
  { id: "MASimple", label: "SMA" },
  { id: "MAExp",    label: "EMA" },
  { id: "BB",       label: "Bollinger Bands" },
  { id: "Volume",   label: "Volume" },
  { id: "RSI",      label: "RSI" },
  { id: "MACD",     label: "MACD" },
  { id: "Stochastic", label: "Stochastic" },
  { id: "ATR",      label: "ATR" },
  { id: "VWAP",     label: "VWAP" },
  { id: "IchimokuCloud", label: "Ichimoku" },
];

const INTERVAL_LABELS = {
  "1": "1m", "5": "5m", "15": "15m", "30": "30m",
  "60": "1H", "240": "4H", "D": "1D", "W": "1W", "M": "1M",
};

export const ChartLayoutsButton = ({
  symbol,
  interval,
  studies,
  onApply,
}) => {
  const { user } = useAuth();
  const isPro = !!(user && (user.tier === "pro" || user.is_admin));
  const [open, setOpen] = useState(false);
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveStudies, setSaveStudies] = useState(studies || []);
  const [saveNotes, setSaveNotes] = useState("");

  const refresh = async () => {
    if (!isPro) return;
    setLoading(true);
    try {
      const r = await listChartLayouts();
      setLayouts(r.layouts || []);
    } catch (e) {
      // Silently ignore — keep the dropdown usable
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && isPro) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isPro]);

  // Default the save form's selected studies to whatever's currently rendered
  useEffect(() => {
    setSaveStudies(studies || []);
  }, [studies]);

  // Auto-suggest a name when the save panel opens
  useEffect(() => {
    if (showSave && !saveName) {
      const intervalLabel = INTERVAL_LABELS[interval] || interval;
      setSaveName(`${(symbol || "").toUpperCase()} ${intervalLabel}`);
    }
  }, [showSave, symbol, interval, saveName]);

  const handleSave = async () => {
    if (!saveName.trim()) {
      toast.error("Give your layout a name first");
      return;
    }
    setSaving(true);
    try {
      const r = await createChartLayout({
        name: saveName.trim(),
        symbol,
        interval,
        studies: saveStudies,
        notes: saveNotes.trim() || null,
      });
      toast.success(`Saved "${r.layout.name}"`);
      setShowSave(false);
      setSaveName("");
      setSaveNotes("");
      refresh();
    } catch (e) {
      toast.error("Couldn't save layout", {
        description: e?.response?.data?.detail || e?.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    try {
      await deleteChartLayout(id);
      toast.success(`Deleted "${name}"`);
      setLayouts((prev) => prev.filter((l) => l.id !== id));
    } catch {
      toast.error("Couldn't delete layout");
    }
  };

  const handleApply = (layout) => {
    onApply?.({
      symbol: layout.symbol,
      interval: layout.interval,
      studies: layout.studies || [],
    });
    toast.success(`Loaded "${layout.name}"`);
    setOpen(false);
  };

  const toggleStudy = (id) => {
    setSaveStudies((prev) => (
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    ));
  };

  // Not signed in? Show a sign-in prompt instead of the full panel.
  if (!isPro) {
    return (
      <button
        type="button"
        title="Sign in to save your TradingView chart presets — free"
        onClick={() => toast("Sign in to save layouts", {
          description: "Save your favorite TradingView chart setups (symbol + interval + indicators) for one-click recall. Free forever — just sign in.",
          icon: <Crown className="w-4 h-4 text-[#00C805]" />,
        })}
        data-testid="chart-layouts-locked"
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-md border border-white/10 text-white/35 hover:text-white/55 hover:border-white/20 transition"
      >
        <Crown className="w-3 h-3" /> Layouts
      </button>
    );
  }

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="chart-layouts-toggle"
        className={
          "inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-md border transition " +
          (open
            ? "bg-[#A98CFF]/15 text-[#A98CFF] border-[#A98CFF]/45"
            : "border-white/10 text-white/60 hover:border-white/25 hover:text-white")
        }
      >
        <Bookmark className="w-3 h-3" />
        Layouts {layouts.length ? `(${layouts.length})` : ""}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1.5 w-[320px] z-30 rounded-lg border border-white/12 bg-[#0a0a0c] shadow-2xl shadow-black/60 p-2"
          data-testid="chart-layouts-dropdown"
        >
          {!showSave && (
            <button
              type="button"
              onClick={() => setShowSave(true)}
              data-testid="chart-layouts-save-current"
              className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md bg-[#A98CFF]/15 border border-[#A98CFF]/30 text-[#A98CFF] hover:bg-[#A98CFF]/25 transition text-[11px] uppercase tracking-[0.18em] mb-2"
            >
              <BookmarkPlus className="w-3.5 h-3.5" />
              Save current chart
            </button>
          )}

          {showSave && (
            <div className="rounded-md border border-white/10 bg-[#050505] p-3 mb-2 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#A98CFF]">
                  Name this preset
                </span>
                <button
                  type="button"
                  onClick={() => { setShowSave(false); setSaveName(""); }}
                  className="text-white/45 hover:text-white"
                  data-testid="chart-layouts-cancel-save"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <input
                type="text"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="e.g. SPY 1H scalp setup"
                maxLength={80}
                data-testid="chart-layouts-name-input"
                className="w-full text-[12px] bg-[#0a0a0c] border border-white/15 rounded px-2.5 py-1.5 text-white placeholder-white/35 focus:outline-none focus:border-[#A98CFF]/55"
                autoFocus
              />
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1.5">
                  Indicators to include
                </div>
                <div className="flex flex-wrap gap-1">
                  {AVAILABLE_STUDIES.map((s) => {
                    const on = saveStudies.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleStudy(s.id)}
                        data-testid={`chart-layouts-study-${s.id}`}
                        className={
                          "text-[10px] px-2 py-0.5 rounded border transition " +
                          (on
                            ? "border-[#A98CFF]/55 bg-[#A98CFF]/15 text-[#A98CFF]"
                            : "border-white/10 text-white/55 hover:border-white/25")
                        }
                      >
                        {on && <Check className="w-2.5 h-2.5 inline mr-0.5" />}
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <input
                type="text"
                value={saveNotes}
                onChange={(e) => setSaveNotes(e.target.value)}
                placeholder="Notes (optional)"
                maxLength={500}
                data-testid="chart-layouts-notes-input"
                className="w-full text-[11px] bg-[#0a0a0c] border border-white/10 rounded px-2.5 py-1.5 text-white/85 placeholder-white/35 focus:outline-none focus:border-[#A98CFF]/35"
              />
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !saveName.trim()}
                data-testid="chart-layouts-confirm-save"
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded bg-[#A98CFF] text-black text-[11px] font-semibold uppercase tracking-[0.18em] hover:bg-[#BFA8FF] disabled:opacity-40 transition"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookmarkPlus className="w-3.5 h-3.5" />}
                Save preset
              </button>
            </div>
          )}

          <div className="max-h-[260px] overflow-y-auto pr-0.5" data-testid="chart-layouts-list">
            {loading && (
              <div className="text-center py-4 text-white/45 text-[11px]">
                <Loader2 className="w-3.5 h-3.5 animate-spin inline" />
              </div>
            )}
            {!loading && layouts.length === 0 && !showSave && (
              <div className="text-center py-6 text-white/45 text-[11px] italic">
                No saved presets yet. Save your first one above.
              </div>
            )}
            {!loading && layouts.map((l) => {
              const isActive = l.symbol === (symbol || "").toUpperCase()
                              && l.interval === interval;
              return (
                <div
                  key={l.id}
                  data-testid={`chart-layouts-item-${l.id}`}
                  className={
                    "flex items-center gap-2 px-2 py-1.5 rounded-md transition group hover:bg-white/[0.04] " +
                    (isActive ? "bg-[#A98CFF]/8 border border-[#A98CFF]/25" : "")
                  }
                >
                  <button
                    type="button"
                    onClick={() => handleApply(l)}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`chart-layouts-apply-${l.id}`}
                  >
                    <div className="text-[12px] text-white/85 truncate">{l.name}</div>
                    <div className="text-[10px] text-white/45 truncate">
                      <span className="num">{l.symbol}</span> · {INTERVAL_LABELS[l.interval] || l.interval}
                      {l.studies?.length ? ` · ${l.studies.length} indicators` : ""}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDelete(l.id, l.name); }}
                    title="Delete preset"
                    data-testid={`chart-layouts-delete-${l.id}`}
                    className="text-white/35 hover:text-[#FF3B30] opacity-0 group-hover:opacity-100 transition"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartLayoutsButton;
